/** Inventory Admin API v2 (`/v1/inventory/admin`) — camelCase request/response types. */

import { SortType } from "../SortType";

export const INVENTORY_VARIANT_SORT_BY = {
  PRODUCT_NAME: "productName",
  QUANTITY: "onHand",
  DATE: "createdAt",
} as const;

export type InventoryVariantSortBy =
  (typeof INVENTORY_VARIANT_SORT_BY)[keyof typeof INVENTORY_VARIANT_SORT_BY];

export const INVENTORY_PACK_SORT_BY = {
  PACK_NAME: "label",
  QUANTITY: "plannedQty",
  DATE: "createdAt",
} as const;

export type InventoryPackSortBy =
  (typeof INVENTORY_PACK_SORT_BY)[keyof typeof INVENTORY_PACK_SORT_BY];

// ── Shared stock / balance ──────────────────────────────────────────────────

export interface InventoryStockBalance {
  clientSku: string;
  productId: number | null;
  onHand: number;
  reserved: number;
  free: number;
}

// ── 1. Movements ────────────────────────────────────────────────────────────

export interface InventoryMovementLine {
  /** Inventory identity — unique per inventory. */
  clientSku: string;
  /** Swageazy catalog SKU for record-keeping (often identical to clientSku). */
  sku: string;
  qty: number;
  productId?: number;
  packSize?: number;
  boxSize?: string;
  tagId?: string;
}

export interface InventoryInboundRequest {
  /** Required when inventoryId is not sent. */
  orderId?: number;
  /** Required when orderId is not sent. */
  inventoryId?: number;
  note?: string;
  lines: InventoryMovementLine[];
}

export interface InventoryDirectDispatchRequest {
  inventoryId: number;
  orderId: number;
  note?: string;
  lines: InventoryMovementLine[];
}

export interface InventoryMovementResult {
  inventoryId: number;
  orderId?: number;
  movementIds: number[];
  stock: InventoryStockBalance[];
}

export interface InventoryAdjustRequest {
  inventoryId: number;
  clientSku: string;
  delta: number;
  note?: string;
}

export type InventoryAdjustResult = InventoryStockBalance;

export interface InventoryAdjustCompleteRequest {
  inventoryId: number;
  clientSkus: string[];
  note: string;
}

export interface InventoryAdjustCompleteStock {
  clientSku: string;
  productId: number | null;
  productName: string;
  onHand: number;
  reserved: number;
  free: number;
}

export interface InventoryAdjustCompleteResult {
  inventoryId: number;
  movementIds: number[];
  stock: InventoryAdjustCompleteStock[];
}

export interface InventoryAdjustImportCsvResult {
  inventoryId: number;
  movementIds: number[];
  stock: InventoryAdjustCompleteStock[];
  sheetUrl: string;
}

export interface InventoryInboundImportCsvResult {
  inventoryId: number;
  movementIds: number[];
  stock: InventoryAdjustCompleteStock[];
  sheetUrl: string | null;
}

export interface InventorySkuImportCsvPackUpdate {
  packId: number;
}

export interface InventorySkuImportCsvLine {
  currentSku: string;
  newSku: string;
  variantId: number;
  packsUpdated: InventorySkuImportCsvPackUpdate[];
}

export interface InventorySkuImportCsvResult {
  inventoryId: number;
  updatedCount: number;
  lines: InventorySkuImportCsvLine[];
  sheetUrl: string | null;
}

// ── 2. Reads ────────────────────────────────────────────────────────────────

export interface InventoryStockFilters {
  inventoryId: number;
  productId?: number;
  includeInactive?: boolean;
  search?: string;
}

export interface InventoryStockResponse {
  inventoryId: number;
  stock: InventoryStockBalance[];
}

export interface InventoryContributionFilters {
  inventoryId: number;
  clientSku?: string;
  orderId?: number;
}

export interface InventoryContribution {
  orderId: number;
  clientSku: string;
  contributedUnits: number;
}

export interface InventoryContributionsResponse {
  inventoryId: number;
  contributions: InventoryContribution[];
}

export interface InventoryOrderSku {
  clientSku: string;
  sku?: string | null;
  contributedUnits: number;
  productId: number | null;
  onHand: number | null;
  reserved: number | null;
  free: number | null;
  packSize?: number | null;
  boxSize?: string | null;
  tagId?: string | null;
  tag?: string | null;
}

export interface InventoryOrderSkusResponse {
  orderId: number;
  inventoryId: number;
  skus: InventoryOrderSku[];
}

export interface InventoryOrderPacksResponse {
  orderId: number;
  inventoryId: number;
  packs: InventoryPackSummary[];
}

export interface InventoryMovementsFilters {
  inventoryId: number;
  clientSku?: string;
  refType?: string;
  refId?: number;
  from?: string;
  to?: string;
  limit?: number;
  cursor?: number;
}

export interface InventoryMovementRow {
  id: number;
  clientSku: string;
  onHandDelta: number;
  reservedDelta: number;
  logType: string;
  refType: string | null;
  refId: number | null;
  packId: number | null;
  adminId: number | null;
  adminName: string | null;
  createdAt: string;
}

export interface InventoryMovementsResponse {
  rows: InventoryMovementRow[];
  nextCursor: number | null;
}

export interface InventoryBuildablePack {
  packId: number;
  label: string;
  plannedQty: number;
  buildableNow: number;
}

export interface InventoryBuildableResponse {
  inventoryId: number;
  packs: InventoryBuildablePack[];
}

// ── 3. Variants ─────────────────────────────────────────────────────────────

export interface InventoryVariantCreateRequest {
  inventoryId: number;
  clientSku: string;
  /** Swageazy catalog SKU for the variant. */
  sku?: string;
  productId: number;
  packSize?: number;
  boxSize?: string;
  tagId?: string;
}

export interface InventoryVariantUpdateRequest {
  clientSku?: string;
  productId?: number;
  packSize?: number;
  boxSize?: string;
  tagId?: string;
}

export interface InventoryVariant {
  id: number;
  inventoryId: number;
  clientSku: string;
  productId: number;
  productName?: string;
  packSize?: number | null;
  boxSize?: string | null;
  tagId?: string | null;
  tag?: string | null;
  onHand: number;
  reserved: number;
  free: number;
  active: number;
  createdAt?: string | null;
  modifiedAt?: string | null;
}

export interface InventoryVariantFilters {
  inventoryId?: number;
  productId?: number;
  includeInactive?: boolean;
  search?: string;
  tagId?: string;
  page_no?: number;
  limit?: number;
  sortBy?: InventoryVariantSortBy | string;
  sortType?: SortType;
}

export interface InventoryVariantsResponse {
  inventoryId: number;
  page_no?: number;
  total_pages?: number;
  total_count?: number;
  limit?: number;
  variants: InventoryVariant[];
}

export interface InventoryVariantSoftDeleteResult {
  id: number;
  clientSku: string;
  active: number;
}

// ── 4. Packs ────────────────────────────────────────────────────────────────

export interface InventoryPackItemInput {
  clientSku: string;
  qty: number;
}

export interface InventoryPackCreateRequest {
  inventoryId?: number | null;
  orderId?: number | null;
  label: string;
  items: InventoryPackItemInput[];
  description?: string;
  plannedQty?: number;
  packImageUrl?: string;
  tagId?: string;
}

export interface InventoryPackUpdateRequest {
  label?: string;
  description?: string;
  plannedQty?: number;
  packImageUrl?: string;
  active?: 0 | 1;
}

export interface InventoryPackReplaceItemsRequest {
  items: InventoryPackItemInput[];
}

export interface InventoryPackItem {
  clientSku: string;
  productId: number | null;
  productName?: string | null;
  qty: number;
}

export interface InventoryPackSummary {
  packId: number;
  inventoryId: number;
  label: string;
  description?: string | null;
  plannedQty: number;
  packImageUrl?: string | null;
  tagId?: string | null;
  productsCount: number;
  active: 0 | 1;
  buildableNow: number;
  inFlight: number;
  shipped: number;
  items?: InventoryPackItem[];
  createdAt?: string | null;
  modifiedAt?: string | null;
}

export interface InventoryPack extends InventoryPackSummary {
  items: InventoryPackItem[];
}

export interface InventoryPackFilters {
  inventoryId: number;
  includeInactive?: boolean;
  search?: string;
  page_no?: number;
  limit?: number;
  sortBy?: InventoryPackSortBy | string;
  sortType?: SortType;
}

export interface InventoryPacksResponse {
  inventoryId: number;
  page_no?: number;
  total_pages?: number;
  total_count?: number;
  limit?: number;
  packs: InventoryPackSummary[];
}

export interface InventoryPackReplaceItemsResult {
  packId: number;
  productsCount: number;
  items: InventoryPackItem[];
}

export interface InventoryCountImportLog {
  id: number;
  customer_id: number;
  customer: string;
  inventory_id: number;
  admin_name: string;
  sheet_url: string;
  row_count: number;
  updated_rows_count: number;
  created_date: string;
}

export interface InventoryCountImportLogsFilters {
  page_no?: number;
  limit?: number;
  search?: string;
  inventoryId?: number;
  customerId?: number;
  adminId?: number;
  start_date?: string;
  end_date?: string;
}

export interface InventoryCountImportLogsResponse {
  page_no: number;
  total_pages: number;
  total_count: number;
  limit: number;
  list: InventoryCountImportLog[];
}

export type InventoryInboundImportLog = InventoryCountImportLog;
export type InventoryInboundImportLogsFilters = InventoryCountImportLogsFilters;
export type InventoryInboundImportLogsResponse = InventoryCountImportLogsResponse;
